import { css } from "@emotion/react";
import { GetStaticPaths, GetStaticProps } from "next";
import Layout from "@/components/Layout/Layout";
import { contentfulClient } from "@/utils/contentful";
import Container from "@/components/Container/Container";
import {
  Body,
  BodyMedium14Css,
  BodyRegular14Css,
  H3Css,
  H4Css,
  H5Css,
  H6Css,
  H7Css,
  Heading,
} from "@/components/Typography";
import Balancer from "react-wrap-balancer";
import startImage from "../../../public/images/sports/start.svg";
import themeImage from "../../../public/images/sports/theme.svg";
import updateImage from "../../../public/images/sports/update.svg";
import Image from "next/image";
import { darkColor, textColor } from "@/constants/colors";
import LinkWithArrow from "@/components/Buttons/LinkWithArrow";
import Link from "next/link";
import {
  backgroundCheckPath,
  communicationPath,
  registrationPath,
  schedulingPath,
  websiteBuilderPath,
} from "@/constants/paths";
import Testimonials from "@/components/Testimonial/Testimonials";
import BottomCTA from "@/components/BottomCTA/BottomCTA";
import { mq } from "@/constants/breakpoints";
import GetStartedButton from "@/components/Buttons/GetStartedButton";
import { useMemo } from "react";
import useSportsData from "@/components/Sports/useSportsData";
import backgroundChecksImage from "../../../public/images/sports/background-checks.png";
import communicationImage from "../../../public/images/sports/communication.png";

interface WebsiteFeatureCardProps {
  imageSrc: string;
  imageAlt: string;
  heading: string;
  body: string;
}

const WebsiteFeatureCard = ({
  imageSrc,
  imageAlt,
  heading,
  body,
}: WebsiteFeatureCardProps) => (
  <div
    css={css`
      text-align: left;
    `}
  >
    <div
      css={css`
        text-align: center;
      `}
    >
      <Image src={imageSrc} alt={imageAlt} />
    </div>
    <Heading
      level={4}
      css={css`
        margin-top: 30px;
        color: ${darkColor};
        ${H7Css}
        ${mq["lg"]} {
          ${H6Css}
        }
      `}
    >
      {heading}
    </Heading>
    <Body
      css={css`
        margin-top: 12px;
        color: ${textColor};
        ${BodyRegular14Css}
      `}
    >
      {body}
    </Body>
  </div>
);

interface OtherFeatureCardProps {
  imageObject?: any;
  heading: string;
  description: string;
  learnMorePath: string;
  reverse?: boolean;
  type: "registration" | "scheduling" | "communication" | "background-check";
  imageSrc?: any;
  sport: string;
}

const OtherFeatureCard = ({
  imageObject,
  heading,
  description,
  learnMorePath,
  type,
  reverse = false,
  imageSrc,
  sport,
}: OtherFeatureCardProps) => {
  const { learnMoreLabel, imageAlt } = useMemo(() => {
    switch (type) {
      case "registration":
        return {
          learnMoreLabel: "Learn about Online Registration",
          imageAlt: `Online Registration for ${sport}`,
        };
      case "scheduling":
        return {
          learnMoreLabel: "Learn about Scheduling",
          imageAlt: `Scheduling for ${sport}`,
        };
      case "background-check":
        return {
          learnMoreLabel: "Learn about Background Checks",
          imageAlt: `Background Checks for ${sport}`,
        };
      case "communication":
        return {
          learnMoreLabel: "Learn about Communication Tools",
          imageAlt: `Communication Tools for ${sport}`,
        };
    }
  }, [type, sport]);

  const imageProps = imageSrc
    ? { src: imageSrc }
    : {
        src: `https:${imageObject.fields.file.url}`,
        width: imageObject.fields.file.details.image.width,
        height: imageObject.fields.file.details.image.height,
      };

  return (
    <div
      css={css`
        margin-top: 60px;
        display: flex;
        gap: 30px;
        flex-direction: column;
        ${mq["md"]} {
          gap: 44px;
          flex-direction: ${reverse ? "row-reverse" : "row"};
        }
      `}
    >
      <div
        css={css`
          flex: 1.15;
        `}
      >
        <Image
          {...imageProps}
          alt={imageAlt}
          css={css`
            height: auto;
            max-width: 100%;
          `}
        />
      </div>
      <div
        css={css`
          flex: 1;
        `}
      >
        <Heading
          level={4}
          css={css`
            color: ${darkColor};
            ${H7Css}
            ${mq["lg"]} {
              ${H4Css}
            }
          `}
        >
          {heading}
        </Heading>
        <Body
          css={css`
            margin-top: 12px;
            white-space: pre-line;
            color: ${textColor};
            ${BodyRegular14Css}
          `}
        >
          {description}
        </Body>
        <div
          css={css`
            margin-top: 24px;
          `}
        >
          <Link href={learnMorePath}>
            <LinkWithArrow>{learnMoreLabel}</LinkWithArrow>
          </Link>
        </div>
      </div>
    </div>
  );
};

const SportPage = ({ fields, testimonials }: any) => {
  const {
    icon,
    heroImageXs,
    heroImageSm,
    heroImageMd,
    heroImageLg,
    bgColor,
    bgMobile,
    bgTablet,
    bgDesktop,
    registrationImage,
    schedulingImage,
  } = useSportsData(fields.type);

  // TODO: we should use same size images for all slugs
  const heroImageHeight =
    fields.slug === "wrestling" || fields.slug === "soccer" ? "476px" : "468px"; // 2 images have slightly different dimensions

  const isOtherBackground = false;

  return (
    <Layout
      title={fields.pageTitle}
      metaDescription={fields.metaDescription}
      heroContainerCss={css`
        background-color: ${bgColor};
        background-image: url("${bgMobile.src}");
        background-size: cover;
        background-position: ${isOtherBackground ? "65%" : "50%"};
        ${mq["sm"]} {
          background-image: url("${bgTablet.src}");
        }
        ${mq["lg"]} {
          background-image: url("${bgDesktop.src}");
          background-position-y: ${fields.slug === "more-sports"
            ? "58px" // text on more-sports page increases hero height
            : undefined};
          background-repeat: no-repeat;
        }
      `}
      hero={
        <Container>
          <div
            css={css`
              text-align: center;
              ${mq["lg"]} {
                display: flex;
                text-align: left;
                gap: 20px;
                padding-top: 30px;
                padding-bottom: 48px;
                align-items: center;
                position: relative;
              }
            `}
          >
            <div
              css={css`
                ${mq["lg"]} {
                  max-width: 557px;
                }
              `}
            >
              <Heading
                level={2}
                css={css`
                  margin-top: 20px;
                  color: ${darkColor};
                  ${H5Css}
                  ${mq["lg"]} {
                    ${H3Css}
                  }
                `}
              >
                <Balancer>
                  {fields.heroHeading}
                  {icon && (
                    <Image
                      src={icon}
                      alt={fields.type}
                      css={css`
                        display: none;
                        margin-left: 10px;
                        margin-bottom: -12px;
                        ${mq["lg"]} {
                          display: inline-block;
                        }
                      `}
                    />
                  )}
                </Balancer>
              </Heading>
              <Body
                css={css`
                  color: ${darkColor};
                  margin-top: 14px;
                  max-width: 560px;
                  margin: 14px auto 0;
                  ${BodyMedium14Css}
                  ${mq["lg"]} {
                    margin-left: 0;
                    margin-right: 0;
                    max-width: 452px;
                    margin-top: 24px;
                  }
                `}
              >
                {fields.heroSubheading}
              </Body>
              <div
                css={css`
                  margin-top: 28px;
                  padding-bottom: ${isOtherBackground ? "40px" : "unset"};
                  a {
                    margin: auto;
                  }
                  ${mq["lg"]} {
                    padding-bottom: 0;
                    margin-top: 40px;
                    a {
                      margin: unset;
                    }
                  }
                `}
              >
                <GetStartedButton />
              </div>
            </div>
            {!isOtherBackground && (
              <div
                css={css`
                  ${mq["sm"]} {
                    margin-left: -40px;
                    margin-right: -40px;
                  }
                  ${mq["md"]} {
                    margin-left: 0;
                    margin-right: 0;
                  }
                  ${mq["lg"]} {
                    height: 468px;
                  }
                `}
              >
                <div
                  css={css`
                    flex: 1;
                    max-width: 100%;
                    padding-top: 86px;
                    img {
                      max-width: 100%;
                      height: auto;
                    }
                    ${mq["xs"]} {
                      position: relative;
                      bottom: -4px;
                    }
                    ${mq["sm"]} {
                      position: relative;
                      padding-top: 50px;
                      padding-bottom: 0;
                      bottom: -3px;
                    }
                    ${mq["md"]} {
                      padding-top: 30px;
                    }
                    ${mq["lg"]} {
                      padding-top: 0;
                      position: absolute;
                      z-index: 0;
                      bottom: 35px;
                      left: calc(50% - 25px);
                      height: ${heroImageHeight};
                      max-width: 975px;
                      width: 100%;
                    }
                  `}
                >
                  <Image
                    src={heroImageXs!}
                    alt={fields.type}
                    css={css`
                      border-top-left-radius: 7.28px;
                      border-top-right-radius: 7.28px;
                      border-bottom-left-radius: 7.28px;
                      border-bottom-right-radius: 7.28px;
                      ${mq["sm"]} {
                        display: none;
                      }
                    `}
                  />
                  <Image
                    src={heroImageSm!}
                    alt={fields.type}
                    css={css`
                      display: none;
                      padding-bottom: 3px;
                      border-top-left-radius: 13.08px;
                      border-top-right-radius: 13.08px;
                      border-bottom-left-radius: 0px;
                      border-bottom-right-radius: 0px;
                      ${mq["sm"]} {
                        display: block;
                      }
                      ${mq["md"]} {
                        display: none;
                      }
                    `}
                  />
                  <Image
                    src={heroImageMd!}
                    alt={fields.type}
                    css={css`
                      display: none;
                      padding-bottom: 3px;
                      ${mq["md"]} {
                        display: block;
                        border-top-left-radius: 14.32px;
                        border-top-right-radius: 14.32px;
                        border-bottom-left-radius: 0px;
                        border-bottom-right-radius: 0px;
                      }
                      ${mq["lg"]} {
                        display: none;
                      }
                    `}
                  />
                  <Image
                    src={heroImageLg!}
                    alt={fields.type}
                    css={css`
                      display: none;
                      border-radius: 16px;
                      ${mq["lg"]} {
                        display: block;
                      }
                    `}
                  />
                </div>
              </div>
            )}
          </div>
        </Container>
      }
    >
      <Container
        css={css`
          padding-top: 60px;
          text-align: center;
        `}
      >
        <Balancer>
          <Heading
            level={3}
            css={css`
              color: ${darkColor};
              ${H5Css}
              ${mq["lg"]} {
                ${H4Css}
              }
            `}
          >
            {fields.websiteSectionHeading}
          </Heading>
        </Balancer>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            margin-top: 60px;
            gap: 80px;
            ${mq["md"]} {
              flex-direction: row;
            }
          `}
        >
          <WebsiteFeatureCard
            imageSrc={startImage}
            imageAlt="Easy to use website builder"
            heading="Get started easily!"
            body="Use Jersey Watch to take your online presence to the next level! Start building your website in just a few minutes and make customizations as you go."
          />
          <WebsiteFeatureCard
            imageSrc={themeImage}
            imageAlt="Custom Website Themes"
            heading="Custom design"
            body="Our webpage templates are built for youth sports and made for busy volunteers. We’ll even help you create a custom design and color scheme so your website stands out online."
          />
          <WebsiteFeatureCard
            imageSrc={updateImage}
            imageAlt="Update your website in minutes"
            heading="Lightning fast updates"
            body="Making changes is easy and you can share access with other board members and coaches."
          />
        </div>
        <div
          css={css`
            padding-top: 40px;
            width: fit-content;
            margin: auto;
          `}
        >
          <Link href={websiteBuilderPath}>
            <LinkWithArrow>Learn about the Website Builder</LinkWithArrow>
          </Link>
        </div>
      </Container>
      <Container>
        <div
          css={css`
            ${mq["md"]} {
              margin-top: 120px;
            }
          `}
        >
          <OtherFeatureCard
            sport={fields.type}
            type="registration"
            learnMorePath={registrationPath}
            imageSrc={registrationImage}
            description={fields.registrationSectionDescription}
            heading={fields.registrationSectionHeading}
          />
          <OtherFeatureCard
            sport={fields.type}
            type="communication"
            learnMorePath={communicationPath}
            imageSrc={communicationImage}
            description={fields.communicationSectionDescription}
            heading={fields.communicationSectionHeading}
            reverse
          />
          <OtherFeatureCard
            sport={fields.type}
            type="scheduling"
            learnMorePath={schedulingPath}
            imageSrc={schedulingImage}
            description={fields.schedulingSectionDescription}
            heading={fields.schedulingSectionHeading}
          />
          <OtherFeatureCard
            sport={fields.type}
            type="background-check"
            learnMorePath={backgroundCheckPath}
            imageSrc={backgroundChecksImage}
            description={fields.backgroundChecksSectionDescription}
            heading={fields.backgroundChecksSectionHeading}
            reverse
          />
        </div>
      </Container>
      <Testimonials testimonials={testimonials} />
      <div
        css={css`
          padding-top: 60px;
        `}
      />
      <BottomCTA />
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const entries = await contentfulClient.getEntries({
    content_type: "sportsPageV2",
  });

  return {
    paths: entries.items.map((entry: any) => ({
      params: {
        slug: entry.fields.slug,
      },
    })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const entries = await contentfulClient.getEntries({
    content_type: "sportsPageV2",
    "fields.slug": ctx.params!.slug,
  });
  const fields = entries.items[0].fields;
  return {
    props: {
      fields,
      testimonials: fields.testimonials.map((t: any) => t.fields),
    },
  };
};

export default SportPage;
